/**
 * <WriteBlock> — render for `lx_write` and `lx_doc_write` tool calls
 * (file create). Single-pane "New file" card with the path + a
 * truncated preview of the body (first PREVIEW_LINES + "..." marker
 * when longer). Same visual family as <EditBlock> minus the diff
 * machinery (the whole body is "added").
 */
import { FilePlus } from "lucide-react";
import type { ToolUseBlock as ToolUseBlockType } from "@/types/content-blocks";

interface WriteBlockProps {
  block: ToolUseBlockType;
}

const PREVIEW_LINES = 20;

function readInput(input: unknown): { path: string; content: string } | null {
  if (!input || typeof input !== "object") return null;
  const r = input as Record<string, unknown>;
  // lx_write uses { path, content }; lx_doc_write uses { docId, content }.
  const path =
    typeof r.path === "string"
      ? r.path
      : typeof r.docId === "string"
        ? r.docId
        : "";
  const content = typeof r.content === "string" ? r.content : "";
  if (!path) return null;
  return { path, content };
}

export function WriteBlock({ block }: WriteBlockProps) {
  const parsed = readInput(block.input);
  if (!parsed) {
    return (
      <div
        data-testid="write-block"
        data-tool-name={block.name}
        data-parse-failed="true"
        className="my-1 rounded-md border border-red-300 bg-red-50/30 px-3 py-2 text-body-sm text-red-700 dark:border-red-700 dark:bg-red-900/15 dark:text-red-300"
      >
        Write block: could not parse <code className="font-mono">{block.name}</code> input.
      </div>
    );
  }
  const { path, content } = parsed;
  const lines = content.split("\n");
  const totalLines = lines.length;
  const previewLines = lines.slice(0, PREVIEW_LINES);
  const truncated = totalLines > PREVIEW_LINES;
  return (
    <div
      data-testid="write-block"
      data-tool-name={block.name}
      className="my-2 overflow-hidden rounded-md border border-green-300 bg-green-50/20 dark:border-green-700 dark:bg-green-900/15"
    >
      <div className="flex items-center justify-between border-b border-green-300 px-3 py-1.5 text-caption dark:border-green-700">
        <span className="inline-flex items-center gap-1.5 font-mono text-coffee-700 dark:text-cream-300">
          <FilePlus size={12} />
          {path}
        </span>
        <span className="text-green-700 dark:text-green-400">New file · {totalLines} line{totalLines === 1 ? "" : "s"}</span>
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap px-3 py-2 font-mono text-body-sm text-coffee-800 dark:text-cream-200">
        {previewLines.join("\n")}
        {truncated && (
          <span className="block text-coffee-500 dark:text-cream-500">… ({totalLines - PREVIEW_LINES} more line{totalLines - PREVIEW_LINES === 1 ? "" : "s"})</span>
        )}
      </pre>
    </div>
  );
}
